import type { GrpcCall } from '../contract';

/**
 * Header stamped on every captured call so the UI recorder can link a recorded
 * UI action to the gRPC call it triggered. Lower-case per the HTTP/2 + gRPC-web
 * convention used by the other PipeEX headers (x-pipeex-user-id, etc.).
 */
export const CORRELATION_ID_HEADER = 'x-pipeex-correlation-id';

/**
 * A gRPC call captured by the recorder. Superset of the frozen contract
 * {@link GrpcCall}: it adds correlation + timing + (when small) inline payloads.
 * When a payload exceeds the contract externalization threshold the recorder
 * leaves only `requestRef` / `responseRef` here and hands the bytes to the sink
 * as an artifact. The archive-assembly card projects this onto the contract.
 */
export interface RecordedGrpcCall extends GrpcCall {
  /** gRPC service typeName, e.g. `pipeex.validation.v1.ValidationService`. */
  service: string;
  /** Per-call correlation id, also stamped on the request header. */
  correlationId: string;
  /** ISO-8601 UTC timestamp when the call left the browser. */
  startedAt: string;
  /** ISO-8601 UTC timestamp when the response (or error) arrived. */
  endedAt: string;
  /** Inline request payload — present only when below the externalization threshold. */
  request?: unknown;
  /** Inline response payload — present only when below the threshold and the call succeeded. */
  response?: unknown;
  /** Error detail for a failed call. */
  error?: string;
}

/**
 * Port the recorder pushes into. The concrete buffer is owned by the UI recorder
 * card and registered via {@link setRecordingSink}; until then the default
 * inactive sink makes the recorder a zero-overhead no-op.
 */
export interface RecordingSink {
  /** True while a recording session is active. */
  isRecording(): boolean;
  /** Atomically reserve the next zero-based step index (drives `step-NNN` ref filenames). */
  reserveStepIndex(): number;
  /** Append a captured call plus any externalized payload artifacts (ref path -> JSON). */
  pushGrpcCall(call: RecordedGrpcCall, artifacts: Record<string, string>): void;
}

const inactiveSink: RecordingSink = {
  isRecording: () => false,
  reserveStepIndex: () => 0,
  pushGrpcCall: () => {
    /* no-op */
  },
};

let activeSink: RecordingSink = inactiveSink;

/** Register the recording buffer (UI recorder card). Pass null to detach. */
export function setRecordingSink(sink: RecordingSink | null): void {
  activeSink = sink ?? inactiveSink;
}

/** The currently registered sink (the inactive no-op sink by default). */
export function getRecordingSink(): RecordingSink {
  return activeSink;
}
